import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { ONBOARDING_STEPS } from './steps';

const OnboardingContext = createContext(null);

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used within OnboardingController');
  }
  return context;
};

const OnboardingController = ({ children, onComplete, onSkip, userId }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [progress, setProgress] = useState(0);

  const getStorageKey = useCallback(() => {
    return userId ? `pickaiOnboarding_${userId}` : 'pickaiOnboarding_anonymous';
  }, [userId]);

  useEffect(() => {
    try {
      const storageKey = getStorageKey();
      const completed = localStorage.getItem(`${storageKey}_completed`);
      const version = localStorage.getItem(`${storageKey}_version`);
      const currentVersion = '1.0.0';

      if (completed === 'true' && version === currentVersion) {
        setHasCompleted(true);
      }
    } catch (error) {
      // Handle localStorage access errors (e.g., private browsing mode)
      console.warn('Onboarding: Could not access localStorage', error);
      setHasCompleted(false);
    }
  }, [getStorageKey]);

  useEffect(() => {
    if (currentStep >= 0 && ONBOARDING_STEPS.length > 0) {
      setProgress(((currentStep + 1) / ONBOARDING_STEPS.length) * 100);
    }
  }, [currentStep]);

  const startOnboarding = useCallback(() => {
    if (!hasCompleted) {
      setIsActive(true);
      setCurrentStep(0);
      document.body.style.overflow = 'hidden';
    }
  }, [hasCompleted]);

  const skipOnboarding = useCallback(() => {
    const storageKey = getStorageKey();
    setIsActive(false);
    setCurrentStep(0);
    document.body.style.overflow = 'unset';
    try {
      localStorage.setItem(`${storageKey}_skipped`, 'true');
    } catch (error) {
      console.warn('Onboarding: Could not save skip state', error);
    }
    onSkip?.();
  }, [onSkip, getStorageKey]);

  const completeOnboarding = useCallback(() => {
    const storageKey = getStorageKey();
    setIsActive(false);
    setHasCompleted(true);
    document.body.style.overflow = 'unset';
    try {
      localStorage.setItem(`${storageKey}_completed`, 'true');
      localStorage.setItem(`${storageKey}_version`, '1.0.0');
    } catch (error) {
      console.warn('Onboarding: Could not save completion state', error);
    }
    onComplete?.();
  }, [onComplete, getStorageKey]);

  const nextStep = useCallback(() => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  }, [currentStep, completeOnboarding]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < ONBOARDING_STEPS.length) {
      setCurrentStep(stepIndex);
    }
  }, []);

  const resetOnboarding = useCallback(() => {
    const storageKey = getStorageKey();
    localStorage.removeItem(`${storageKey}_completed`);
    localStorage.removeItem(`${storageKey}_skipped`);
    localStorage.removeItem(`${storageKey}_version`);
    setHasCompleted(false);
    setCurrentStep(0);
    setIsActive(false);
  }, [getStorageKey]);

  const value = {
    isActive,
    currentStep,
    hasCompleted,
    progress,
    totalSteps: ONBOARDING_STEPS.length,
    currentStepData: ONBOARDING_STEPS[currentStep],
    startOnboarding,
    skipOnboarding,
    completeOnboarding,
    nextStep,
    previousStep,
    goToStep,
    resetOnboarding,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === ONBOARDING_STEPS.length - 1,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingController;